<div class="flex justify-content-center px-5 pt-5 pb-4 border-bottom-1 border-neutral-400 bg-white">
  <div class="w-full max-w-60rem text-neutral-800">
    <h1 class="text-xl font-semibold m-0 mb-2">Third-Party Verification</h1>
    <p class="text-base font-normal m-0">Enter an order number to see and verify order information.</p>
  </div>
</div>

<div class="w-full max-w-60rem mx-auto px-3 pt-3 pb-5 flex flex-column gap-3">
  <form class="bg-neutral-100 border-1 border-neutral-200 border-round-lg px-6 py-5 flex flex-column align-items-center gap-4 text-neutral-800"
    [formGroup]="searchForm" (ngSubmit)="search()" aria-labelledby="tpvOrderSearch">
    <h2 id="tpvOrderSearch" class="text-base font-semibold m-0">Order Search</h2>
    <div class="w-full flex flex-column md:flex-row justify-content-between gap-4">
      <div class="flex-1 flex flex-column align-items-center gap-3">
        <label for="tpvOrderNumber" class="text-xl font-semibold">Order Number</label>
        <p-iconField iconPosition="right">
          <p-inputIcon styleClass="pi pi-search" />
          <input #orderNumberInput id="tpvOrderNumber" pInputText formControlName="orderNumber" pKeyFilter="alphanum" maxlength="20"
            class="w-19rem max-w-full h-3rem pl-3 pr-6 text-base border-round-lg border-neutral-600"
            placeholder="Enter TPV order number" autocomplete="off"
            [class.ng-dirty]="searchForm.controls.orderNumber.touched"
            [attr.aria-invalid]="searchForm.controls.orderNumber.touched && searchForm.controls.orderNumber.invalid"
            aria-describedby="tpvOrderNumberError" />
        </p-iconField>
        @if (searchForm.controls.orderNumber.touched && searchForm.controls.orderNumber.invalid) {
          <small id="tpvOrderNumberError" class="p-error">Order Number is required.</small>
        }
      </div>
      <div class="flex-1 flex flex-column align-items-center gap-3">
        <label for="tpvCallerNumber" class="text-xl font-semibold">Caller Number <span class="text-base font-normal">(optional)</span></label>
        <p-iconField iconPosition="right">
          <p-inputIcon styleClass="pi pi-search" />
          <input id="tpvCallerNumber" pInputText formControlName="callerNumber" pKeyFilter="pint" inputmode="numeric" maxlength="10"
            class="w-19rem max-w-full h-3rem pl-3 pr-6 text-base border-round-lg border-neutral-600"
            placeholder="Enter caller phone number" autocomplete="off" />
        </p-iconField>
      </div>
    </div>
    <hr class="w-full m-0" />
    <p-button type="submit" label="SEARCH" styleClass="w-19rem max-w-full text-lg font-semibold border-round-lg"
      [loading]="searching()" [disabled]="searching()" />
  </form>

  @if (result()?.SearchStatus === 'NotFound') {
    <div class="flex align-items-center gap-3 bg-neutral-100 border-1 border-neutral-300 border-round-lg px-4 py-3" role="status">
      <i class="pi pi-info-circle" aria-hidden="true"></i>
      <span class="flex-1 text-sm font-normal text-neutral-800">No matching TPV orders found, please try again.</span>
      <button type="button" class="p-link" aria-label="Close" (click)="result.set(null)"><i class="pi pi-times text-sm"></i></button>
    </div>
  } @else if (result()) {
    <hr class="w-full m-0 border-neutral-400" />
    <div class="flex align-items-stretch gap-2 py-2">
      <div class="flex align-items-center gap-2 p-2 bg-white border-1 border-neutral-400 border-round-lg">
        <span class="bg-brand-bg border-1 border-sara-dark-blue border-round px-2 text-sm font-semibold font-product">Order</span>
        <span class="text-xl font-semibold text-neutral-800">{{ result().OrderNumber }}</span>
      </div>
      @if (statusLabel; as status) {
        <span class="flex align-items-center px-2 border-1 border-round-lg text-sm font-semibold" [ngClass]="STATUS_CLASSES[status]">{{ status }}</span>
      }
    </div>

    @if (result().SearchStatus === 'Expired') {
      <div class="flex align-items-center gap-3 bg-neutral-100 border-1 border-neutral-400 border-round-lg px-4 py-3" role="status">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span class="text-sm font-normal text-neutral-800">This order has expired and cannot be recovered.</span>
      </div>
    } @else if (detailsLoading()) {
      <div class="flex justify-content-center p-4" role="status" aria-label="Loading order details">
        <p-progressSpinner styleClass="w-3rem h-3rem" strokeWidth="4" />
      </div>
    } @else if (detailsError()) {
      <div class="flex align-items-center gap-3 bg-neutral-100 border-1 border-neutral-400 border-round-lg px-4 py-3" role="alert">
        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
        <span class="text-sm font-normal text-neutral-800">Order details could not be loaded. Please try again.</span>
      </div>
    } @else if (details()?.Header) {
      @let h = details().Header;
      @let store = details().StoreDetails;
      @let salesperson = details().Salesperson;
      <div class="flex flex-column md:flex-row align-items-start gap-3 text-sm text-neutral-800">
        <section class="w-full md:flex-1 flex flex-column gap-2" aria-labelledby="tpvCustomerInfo">
          <h2 id="tpvCustomerInfo" class="m-0 py-2 text-base font-semibold text-center text-white bg-sara-dark-blue border-round-lg">Customer Information</h2>
          <dl class="m-0 bg-white border-1 border-neutral-400 border-round-lg overflow-hidden">
            <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Customer Name</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ ((h.CustFirstName ?? '') + ' ' + (h.CustLastName ?? '')).trim() || '—' }}</dd></div>
            <div class="flex py-2"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Service Address</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ h.StreetAddress || '—' }}@if (h.CityStateZip) {<br />{{ h.CityStateZip }}}</dd></div>
            <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Primary Phone</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ (h.CustPhone | phone) || '—' }}</dd></div>
            <div class="flex py-2"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Alternate Phone</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ (h.CustAltPhone | phone) || '—' }}</dd></div>
            <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Email Address</dt><dd class="m-0 pl-2 min-w-0 font-semibold wrap-text">{{ h.CustEmail || '—' }}</dd></div>
          </dl>
        </section>
        <div class="w-full md:flex-1 flex flex-column gap-3">
          @if (store?.Retailer || store?.Store) {
            <section class="flex flex-column gap-2" aria-labelledby="tpvStoreDetails">
              <h2 id="tpvStoreDetails" class="m-0 py-2 text-base font-semibold text-center text-white bg-sara-dark-blue border-round-lg">Store Details</h2>
              <dl class="m-0 bg-white border-1 border-neutral-400 border-round-lg overflow-hidden">
                <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Retailer</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ store.Retailer || '—' }}</dd></div>
                <div class="flex py-2"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Store</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ store.Store || '—' }}</dd></div>
              </dl>
            </section>
          }
          <section class="flex flex-column gap-2" aria-labelledby="tpvAgentInfo">
            <h2 id="tpvAgentInfo" class="m-0 py-2 text-base font-semibold text-center text-white bg-sara-dark-blue border-round-lg">Agent Information</h2>
            <dl class="m-0 bg-white border-1 border-neutral-400 border-round-lg overflow-hidden">
              <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Agent Name</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ salesperson?.ProperName || '—' }}</dd></div>
              <div class="flex py-2"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Agent ATT UID</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ salesperson?.AttUid || '—' }}</dd></div>
              @if (salesperson?.LocationName) {
                <div class="flex py-2 bg-neutral-200"><dt class="w-10rem flex-shrink-0 pl-3 font-normal">Location</dt><dd class="m-0 pl-2 min-w-0 font-semibold">{{ salesperson.LocationName }}</dd></div>
              }
            </dl>
          </section>
        </div>
      </div>
    }
  }
</div>

<p-dialog [visible]="showCompletedDialog()" (visibleChange)="showCompletedDialog.set($event)"
  [modal]="true" [draggable]="false" [resizable]="false" styleClass="w-11 md:w-26rem border-round-3xl tpv-completed-dialog">
  <ng-template pTemplate="header">
    <span class="text-xl font-semibold text-neutral-800">TPV Completed</span>
  </ng-template>
  <p class="m-0 text-base font-normal text-neutral-800">The TPV for this order has already been completed. You can view order info only.</p>
  <ng-template pTemplate="footer">
    <p-button label="OK" styleClass="border-round-lg" (onClick)="showCompletedDialog.set(false)" />
  </ng-template>
</p-dialog>
